// Phase 2.2.6 — Reusable informational popup for Infrastructure Security controls.
// Read-only / editorial. Does NOT affect scoring.

import React from "react";
import { Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  getInfrastructureControlMetadata,
  type InfrastructureControlMetadata,
} from "@/configs/controlGuidance/infrastructureControlMetadata";
import {
  TELECOM_MODULES,
  SERVICE_MODEL_MODULE_APPLICABILITY,
  TELECOM_SERVICE_MODELS,
  type TelecomModuleId,
  type TelecomServiceModel,
} from "@/configs/telecom/telecomApplicabilityMatrix";

function moduleLabelFor(moduleId: string): string {
  const hit = TELECOM_MODULES.find((m) => m.id === moduleId);
  return hit ? `${hit.id} — ${hit.label}` : moduleId;
}

function serviceModelsForModule(moduleId: string): string[] {
  const mod = moduleId as TelecomModuleId;
  const out: string[] = [];
  for (const sm of TELECOM_SERVICE_MODELS) {
    const mods = SERVICE_MODEL_MODULE_APPLICABILITY[sm as TelecomServiceModel];
    if (mods && mods.includes(mod) && !out.includes(sm)) out.push(sm);
  }
  return out;
}

interface Props {
  controlKey: string;
  /** Optional label fallback shown when metadata is missing. */
  fallbackLabel?: string;
}

export const InfrastructureControlInfoPopup: React.FC<Props> = ({
  controlKey,
  fallbackLabel,
}) => {
  const meta = getInfrastructureControlMetadata(controlKey);
  if (!meta) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[InfrastructureControlInfoPopup] missing metadata for control_key=${controlKey}`,
      );
    }
    return null;
  }

  return (
    <HoverCard openDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`${meta.label} guidance`}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="start"
        sideOffset={8}
        className="z-[9999] w-[28rem] max-w-[28rem] max-h-[70vh] overflow-y-auto text-foreground"
      >
        <PopupBody meta={meta} fallbackLabel={fallbackLabel} />
      </HoverCardContent>
    </HoverCard>
  );
};

function PopupBody({
  meta,
  fallbackLabel,
}: {
  meta: InfrastructureControlMetadata;
  fallbackLabel?: string;
}) {
  const title = meta.label || fallbackLabel || meta.controlKey;
  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold">{title}</div>
          <span className="inline-flex items-center rounded-full border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {meta.module}
          </span>
        </div>
        {meta.description && (
          <p className="text-xs text-muted-foreground mt-1">{meta.description}</p>
        )}
      </div>

      {/* Phase 2.9.1 — Service-model-aware applicability. */}
      {meta.module && meta.module.startsWith("C") && (() => {
        const models = serviceModelsForModule(meta.module);
        return (
          <>
            {models.length > 0 && (
              <Section heading="Applies to Service Models">
                <PillRow items={models} />
              </Section>
            )}
            <Section heading="Telecom Domain">
              <PillRow items={[moduleLabelFor(meta.module)]} variant="muted" />
            </Section>
          </>
        );
      })()}

      {meta.whyItMatters && (
        <Section heading="Why it matters">
          <p className="text-muted-foreground">{meta.whyItMatters}</p>
        </Section>
      )}

      {meta.nistMappings.length > 0 && (
        <Section heading="NIST CSF 2.0 mappings">
          <PillRow items={meta.nistMappings} />
        </Section>
      )}

      {meta.telecomStandards.length > 0 && (
        <Section heading="Telecom / industry standards">
          <PillRow items={meta.telecomStandards} variant="muted" />
        </Section>
      )}

      {meta.evidenceExamples.length > 0 && (
        <Section heading="Evidence examples">
          <BulletList items={meta.evidenceExamples} />
        </Section>
      )}

      {(meta.assessmentRubric.implemented ||
        meta.assessmentRubric.partial ||
        meta.assessmentRubric.notImplemented) && (
        <Section heading="Assessment guidance">
          <dl className="space-y-1.5">
            <RubricRow
              label="Implemented"
              value={meta.assessmentRubric.implemented}
              tone="ok"
            />
            <RubricRow
              label="Partial"
              value={meta.assessmentRubric.partial}
              tone="warn"
            />
            <RubricRow
              label="Not Implemented"
              value={meta.assessmentRubric.notImplemented}
              tone="bad"
            />
          </dl>
        </Section>
      )}

      {meta.applicableServiceModels.length > 0 && (
        <Section heading="Applicable service models">
          <PillRow items={meta.applicableServiceModels} variant="muted" />
        </Section>
      )}
    </div>
  );
}

function Section({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-xs">
      <h4 className="font-bold mb-1">{heading}</h4>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  );
}

function PillRow({
  items,
  variant = "default",
}: {
  items: string[];
  variant?: "default" | "muted";
}) {
  const cls =
    variant === "muted"
      ? "bg-muted/40 border-border text-muted-foreground"
      : "bg-muted/60 border-border text-foreground";
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((c) => (
        <span
          key={c}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${cls}`}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function RubricRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "bad";
}) {
  if (!value) return null;
  const dot =
    tone === "ok"
      ? "bg-emerald-500"
      : tone === "warn"
      ? "bg-amber-500"
      : "bg-red-500";
  return (
    <div className="flex items-start gap-2">
      <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <div>
        <dt className="font-medium text-foreground inline">{label}: </dt>
        <dd className="inline text-muted-foreground">{value}</dd>
      </div>
    </div>
  );
}
